import { FC, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import DatePicker, { registerLocale } from 'react-datepicker';
import {
  format,
  parse,
  isValid,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
  subYears,
} from 'date-fns';
import nl from 'date-fns/locale/nl';
import enUS from 'date-fns/locale/en-US';
import de from 'date-fns/locale/de';
import 'react-datepicker/dist/react-datepicker.css';
import './DateRangeDropdown.scss';

registerLocale('nl', nl);
registerLocale('en', enUS);
registerLocale('de', de);

interface DateRangeDropdownProps {
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  isLoading?: boolean;
}

const formatDisplayDate = (date: Date | null): string => {
  if (!date) return '...';
  return format(date, 'yyyy-MM-dd');
};

interface Preset {
  label: string;
  range: () => [Date | null, Date | null];
}

const getPresets = (t: TFunction): Preset[] => [
  { label: t('This Month'), range: () => [startOfMonth(new Date()), new Date()] },
  { label: t('Last Month'), range: () => [startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))] },
  { label: t('Last Full Quarter'), range: () => [startOfQuarter(subQuarters(new Date(), 1)), endOfQuarter(subQuarters(new Date(), 1))] },
  { label: t('Last 12 Months'), range: () => [startOfMonth(subMonths(new Date(), 11)), new Date()] },
  { label: t('This Year'), range: () => [startOfYear(new Date()), new Date()] },
  { label: t('Last Year'), range: () => [startOfYear(subYears(new Date(), 1)), endOfYear(subYears(new Date(), 1))] },
  { label: t('All Dates'), range: () => [null, null] },
];

const DateRangeDropdown: FC<DateRangeDropdownProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [startInput, setStartInput] = useState('');
  const [endInput, setEndInput] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync input fields when props change (from calendar/presets)
  useEffect(() => {
    setStartInput(startDate ? format(startDate, 'yyyy-MM-dd') : '');
  }, [startDate]);

  useEffect(() => {
    setEndInput(endDate ? format(endDate, 'yyyy-MM-dd') : '');
  }, [endDate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isLoading) {
      setIsOpen(!isOpen);
    }
  };

  const getButtonText = () => {
    if (!startDate && !endDate) {
      return t('All Dates');
    }
    return `${formatDisplayDate(startDate)} — ${formatDisplayDate(endDate)}`;
  };

  const handlePresetClick = (preset: Preset) => {
    const [start, end] = preset.range();
    onStartDateChange(start);
    onEndDateChange(end);
    setIsOpen(false);
  };

  const handleRangeChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    onStartDateChange(start);
    onEndDateChange(end);
  };

  const commitStartInput = () => {
    if (!startInput) {
      onStartDateChange(null);
      return;
    }
    const parsed = parse(startInput, 'yyyy-MM-dd', new Date());
    if (isValid(parsed)) {
      onStartDateChange(parsed);
    } else {
      setStartInput(startDate ? format(startDate, 'yyyy-MM-dd') : '');
    }
  };

  const commitEndInput = () => {
    if (!endInput) {
      onEndDateChange(null);
      return;
    }
    const parsed = parse(endInput, 'yyyy-MM-dd', new Date());
    if (isValid(parsed)) {
      onEndDateChange(parsed);
    } else {
      setEndInput(endDate ? format(endDate, 'yyyy-MM-dd') : '');
    }
  };

  const presets = getPresets(t);

  const isPresetActive = (preset: Preset): boolean => {
    const [pStart, pEnd] = preset.range();
    if (pStart === null && pEnd === null) {
      return startDate === null && endDate === null;
    }
    if (!startDate || !endDate || !pStart || !pEnd) return false;
    return (
      format(startDate, 'yyyy-MM-dd') === format(pStart, 'yyyy-MM-dd') &&
      format(endDate, 'yyyy-MM-dd') === format(pEnd, 'yyyy-MM-dd')
    );
  };

  return (
    <div className="date-range-dropdown" ref={dropdownRef}>
      <label
        className="date-range-dropdown__label"
        htmlFor="date-range-dropdown-toggle"
      >
        {t('Date Range')}
      </label>
      <button
        type="button"
        id="date-range-dropdown-toggle"
        className={`date-range-dropdown__button ${isOpen ? 'date-range-dropdown__button--open' : ''}`}
        onClick={handleToggle}
        disabled={isLoading}
        aria-label={t('Date range filter')}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span className="date-range-dropdown__button-text">
          {getButtonText()}
        </span>
        <span className={`date-range-dropdown__button-icon ${isOpen ? 'date-range-dropdown__button-icon--open' : ''}`}>
          <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="currentColor">
            <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502a1.095 1.095 0 0 1-1.576 0S4.924 9.581 4.516 9.163c-0.408-0.418-0.436-1.17 0-1.615z" />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className="date-range-dropdown__panel">
          <div className="date-range-dropdown__presets">
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                className={`date-range-dropdown__preset-btn ${isPresetActive(preset) ? 'date-range-dropdown__preset-btn--active' : ''}`}
                onClick={() => handlePresetClick(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="date-range-dropdown__calendar-area">
            <div className="date-range-dropdown__date-inputs">
              <div className="date-range-dropdown__date-field">
                <label className="date-range-dropdown__date-label" htmlFor="date-range-start-input">{t('Start Date')}</label>
                <input
                  id="date-range-start-input"
                  type="text"
                  className="date-range-dropdown__date-input"
                  value={startInput}
                  onChange={(e) => setStartInput(e.target.value)}
                  onBlur={commitStartInput}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitStartInput(); }}
                  placeholder="YYYY-MM-DD"
                  autoComplete="off"
                />
              </div>
              <span className="date-range-dropdown__date-separator">→</span>
              <div className="date-range-dropdown__date-field">
                <label className="date-range-dropdown__date-label" htmlFor="date-range-end-input">{t('End Date')}</label>
                <input
                  id="date-range-end-input"
                  type="text"
                  className="date-range-dropdown__date-input"
                  value={endInput}
                  onChange={(e) => setEndInput(e.target.value)}
                  onBlur={commitEndInput}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitEndInput(); }}
                  placeholder="YYYY-MM-DD"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="date-range-dropdown__calendar">
              <DatePicker
                selected={startDate}
                onChange={handleRangeChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                inline
                monthsShown={2}
                // i18n.language carries the full BCP-47 tag the browser
                // reports (e.g. "en-GB", "nl-NL"), but only the base codes are
                // registered above — react-datepicker then logs "A locale
                // object was not found" and silently falls back to US
                // conventions for week-start and date order.
                locale={i18n.language.split('-')[0]}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeDropdown;
